import { useMemo } from "react";
import { useSeo, getSiteUrl } from "@/hooks/useSeo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ContactForm from "./components/ContactForm";

const stats = [
  { value: "5年+", label: "稳定运营" },
  { value: "1200+", label: "收录作品" },
  { value: "8万+", label: "总章节数" },
  { value: "500万+", label: "注册读者" },
];

const honors = [
  { icon: "ri-shield-check-line", title: "正规备案", desc: "网站依法合规运营,内容持续优化" },
  { icon: "ri-flashlight-line", title: "极速加载", desc: "全站 CDN 加速,高清图快速加载" },
  { icon: "ri-heart-2-line", title: "阅读无扰", desc: "沉浸式阅读体验,广告打扰少" },
  { icon: "ri-team-line", title: "贴心客服", desc: "工作日 9:00-22:00 在线答疑" },
];

const testimonials = [
  {
    name: "阿泽,大学生",
    quote: "从初中开始追家庭教师,现在还能在土豪漫画网一口气重温全集,画质清晰,太感动了。",
  },
  {
    name: "小喵,上班族",
    quote: "每天午休看两话,更新很及时,界面也干净清爽,已经安利给全办公室了。",
  },
  {
    name: "老张,十年漫迷",
    quote: "热血少年漫画的宝藏站,完结作品和连载中的都有,找漫画非常方便。",
  },
];

const contactCards = [
  {
    icon: "ri-mail-line",
    label: "商务合作邮箱",
    value: "service@tuhaomanhua.com",
    note: "版权合作 / 商务洽谈",
  },
  {
    icon: "ri-qq-line",
    label: "官方读者群",
    value: "88881234",
    note: "每日更新提醒与福利",
  },
  {
    icon: "ri-wechat-line",
    label: "官方微信",
    value: "tuhao_manga",
    note: "搜索添加公众号",
  },
  {
    icon: "ri-time-line",
    label: "客服时间",
    value: "每日 9:00 - 22:00",
    note: "留言会在 24 小时内回复",
  },
];

export default function About() {
  const jsonLd = useMemo(() => {
    const url = getSiteUrl("/about");
    return [
      {
        "@context": "https://storyvault3.github.io",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: getSiteUrl("/") },
          { "@type": "ListItem", position: 2, name: "关于我们", item: url },
        ],
      },
      {
        "@context": "https://storyvault3.github.io",
        "@type": "AboutPage",
        name: "关于我们-土豪漫画网",
        url,
        description: "了解土豪漫画网的品牌故事、口碑荣誉与联系方式,一起免费阅读《家庭教师》等热血漫画。",
        inLanguage: "zh-CN",
      },
    ];
  }, []);

  useSeo({
    title: "关于我们-土豪漫画网",
    description:
      "土豪漫画网致力于为用户提供《家庭教师》等热血少年漫画的免费在线阅读服务,高清画质、每日更新、运营稳定,期待与你一起畅享漫画世界。",
    keywords: "关于我们,土豪漫画网,免费阅读,家庭教师,联系方式",
    path: "/about",
    jsonLd,
  });

  return (
    <div className="bg-background-100/50">
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">关于我们</h1>
          <p className="mt-2 max-w-2xl text-sm text-foreground-500">
            了解土豪漫画网的品牌故事、口碑荣誉与联系方式。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-10 md:px-6">
        <section className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-center" aria-labelledby="brand-heading">
          <div className="relative overflow-hidden rounded-lg">
            <img
              src="https://readdy.ai/api/search-image?query=Anime%20illustration%20of%20a%20cozy%20modern%20manga%20publishing%20studio%20workspace%20with%20warm%20desk%20lamps%2C%20shelves%20full%20of%20manga%20volumes%2C%20colorful%20manga%20posters%20on%20the%20walls%2C%20a%20desk%20with%20a%20laptop%20and%20sketchbook%2C%20soft%20warm%20lighting%2C%20inviting%20atmosphere%2C%20highly%20detailed%20anime%20background%20art&width=900&height=600&seq=about-studio-001&orientation=landscape"
              alt="土豪漫画网工作室环境插画"
              title="土豪漫画网,漫画爱好者的阅读家园"
              className="h-72 w-full object-cover object-top md:h-96"
            />
          </div>
          <div>
            <div className="flex items-center gap-2.5">
              <span className="h-6 w-1.5 rounded-full bg-primary-500" aria-hidden="true" />
              <h2 id="brand-heading" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                我们的品牌故事
              </h2>
            </div>
            <p className="mt-4 text-sm leading-7 text-foreground-600">
              土豪漫画网创立于 2021 年,是一群热爱热血少年漫画的读者共同搭建的免费阅读平台。我们最初的想法很简单:让每一个喜欢《家庭教师》这样经典作品的人,都能没有门槛地重温故事、畅快追更。
            </p>
            <p className="mt-3 text-sm leading-7 text-foreground-600">
              多年来,我们坚持「免费、高清、稳定」三大原则,不断优化阅读体验,并持续收录更多人气作品。从热血冒险到校园搞笑,从完结经典到连载新作,我们希望能成为每一位漫画爱好者最顺手的追更小窝。
            </p>
            <p className="mt-3 text-sm leading-7 text-foreground-600">
              未来,我们还会上线书架收藏、追更提醒等新功能。感谢每一位读者的陪伴,让我们一起把这份对漫画的热爱传递下去。
            </p>
          </div>
        </section>

        <section className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-4" aria-label="网站数据">
          {stats.map((stat) => (
            <div key={stat.label} className="rounded-lg border border-background-200 bg-background-50 px-4 py-6 text-center">
              <p className="font-heading text-2xl font-bold text-primary-600">{stat.value}</p>
              <p className="mt-1 text-sm text-foreground-500">{stat.label}</p>
            </div>
          ))}
        </section>

        <section className="mt-14" aria-labelledby="reputation-heading">
          <div className="flex items-center gap-2.5">
            <span className="h-6 w-1.5 rounded-full bg-accent-500" aria-hidden="true" />
            <h2 id="reputation-heading" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              口碑与荣誉
            </h2>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {honors.map((honor) => (
              <div key={honor.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-100 text-xl text-accent-700">
                  <i className={honor.icon} aria-hidden="true" />
                </span>
                <h3 className="mt-3 text-sm font-bold text-foreground-950">{honor.title}</h3>
                <p className="mt-1 text-xs leading-relaxed text-foreground-500">{honor.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
            {testimonials.map((t) => (
              <figure key={t.name} className="rounded-lg border border-background-200 bg-secondary-50 p-5">
                <i className="ri-double-quotes-l text-xl text-secondary-500" aria-hidden="true" />
                <blockquote className="mt-2 text-sm leading-relaxed text-foreground-700">{t.quote}</blockquote>
                <figcaption className="mt-3 text-xs font-medium text-foreground-500">—— {t.name}</figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="mt-14" aria-labelledby="contact-heading">
          <div className="flex items-center gap-2.5">
            <span className="h-6 w-1.5 rounded-full bg-primary-500" aria-hidden="true" />
            <h2 id="contact-heading" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              联系我们
            </h2>
          </div>
          <p className="mt-2 text-sm text-foreground-500">
            无论是想看的漫画、阅读建议还是合作意向,欢迎随时联系我们。
          </p>

          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-5">
            <div className="flex flex-col gap-4 lg:col-span-2">
              {contactCards.map((card) => (
                <div key={card.label} className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 text-lg text-primary-600">
                    <i className={card.icon} aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold text-foreground-950">{card.label}</h3>
                    <p className="mt-0.5 truncate text-sm text-foreground-700">{card.value}</p>
                    <p className="mt-0.5 text-xs text-foreground-400">{card.note}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="lg:col-span-3">
              <ContactForm />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}